/**
 * What the player screen shows around the end of an adventure (owner 2026-10-11).
 *
 * TWIN COPY. The same file lives at
 * `ExpoMobile/src/features/player/completion/endingPhase.ts`. It is pure TypeScript (no
 * React, no platform code) so both players share one state machine and one set of tests. Change
 * both together. (2026-10-13: ported the phone's `liveHere`, "seen playing is always ended",
 * undated = ended, and UTC parsing of offset-less dates; the two copies match again.)
 *
 * Reaching an exit used to tear the player down at once: the bottom bar (Journal, References,
 * Map, the read-aloud controls) went and the results took over. An adventure that is mostly
 * narration ends by itself, so a player never got to open its References or replay its last
 * entry. Now the session still ends exactly when the server (or the phone's own engine) says it
 * does, with completion, stats and achievements untouched; only the screen waits:
 *
 * - `loading`  nothing is known about this session yet.
 * - `playing`  the adventure is running.
 * - `ended`    it has ended, but the player is still in it: every button works, the journal shows
 *              its last entries, and a Finish button sits at the bottom of the journal.
 * - `results`  the completed screen. Reached from `ended` by Finish (or by leaving), or straight
 *              away when the screen opens on a session that ended a while ago, or when the player
 *              ended it themselves from the exit control.
 *
 * `ended` and `results` never go back: once a session has ended, nothing on this screen can make
 * it run again, and a poll that briefly disagrees must not flicker the results away.
 *
 * The completed screen still opened by itself (owner 2026-10-13). The online state carries no
 * `completedAt`, so a session whose FIRST state was already terminal (a narration-only stop, a
 * check-in Story stop that completes inside the join, a completion that landed before the first
 * load) could not be dated and went to the results under the old "undated means reopened" rule.
 * Now: an ending this screen saw coming (`playing` first), or on a session this device just
 * started or saw running, is always `ended`; only a session dated as long finished goes straight
 * to the results, and an undated one is `ended` (Finish is one click away).
 */

export type EndingPhase = 'loading' | 'playing' | 'ended' | 'results'

/** How the adventure ended, as the results screen words it. */
export type EndingOutcome = 'completed' | 'failed' | 'ended'

/**
 * How long after the session ended an opened screen still counts as "just finished".
 *
 * An adventure that is only narration can reach its exit inside the very request that starts it,
 * so the first state the screen ever reads is already terminal. Judged by the age of `completedAt`
 * rather than by "was it running when the screen opened": a session that ended moments ago is the
 * one the player is playing now, and one that ended long ago is being looked back at. Generous,
 * because the server's clock and the device's are not the same clock.
 */
export const JUST_ENDED_MS = 5 * 60_000

export interface EndingInput {
  /** The screen holds this session's state (not a previous session's, not mid-first-load). */
  ready: boolean
  /** The session has ended: a terminal status, or a completion summary. */
  terminal: boolean
  /** When it ended, epoch ms; null when the state did not say. */
  completedAtMs: number | null
  nowMs: number
  /**
   * The player ended it themselves (the exit control's Finish / Give up / Abandon, which already
   * asked them to confirm), or already pressed Finish on it earlier on this device. Either way
   * there is no ending left to linger on.
   */
  acknowledged: boolean
  /**
   * This device started the session, or saw it running, since the page loaded. Its ending is the
   * one being played now, whatever the two clocks say.
   */
  liveHere: boolean
}

/** The phase after reading the session once more. Pure; see the file header for the rules. */
export function nextEndingPhase(prev: EndingPhase, input: EndingInput): EndingPhase {
  if (prev === 'ended' || prev === 'results') return prev
  if (!input.ready) return prev
  if (!input.terminal) return 'playing'
  if (input.acknowledged) return 'results'
  // It ended while this screen was showing it run (only ever this screen's own load, see `ready`).
  if (prev === 'playing') return 'ended'
  if (input.liveHere) return 'ended'
  if (input.completedAtMs != null && input.nowMs - input.completedAtMs > JUST_ENDED_MS) return 'results'
  // Just ended, or cannot be dated: keep the player, Finish is one click away.
  return 'ended'
}

/** Finish (or leaving) while `ended`: on to the results. Anything else stays where it is. */
export function finishEnding(prev: EndingPhase): EndingPhase {
  return prev === 'ended' ? 'results' : prev
}

/** The words for an ending still on screen: the header chip and the button under the journal. */
export function endingCopy(outcome: EndingOutcome): { chip: string; button: string } {
  if (outcome === 'completed') return { chip: 'Adventure complete', button: 'Finish' }
  if (outcome === 'failed') return { chip: 'Adventure over', button: 'See results' }
  return { chip: 'Adventure ended', button: 'See results' }
}

/**
 * Parse a wire date, or null. The server sends some `DateTime`s with no offset; they are UTC, and
 * parsed as local they would be off by the reader's own offset (hours, in the US), which is enough
 * to make a session that ended a second ago look like one that ended this morning.
 */
export function endedAtMs(completedAt: string | null | undefined): number | null {
  if (!completedAt) return null
  const iso = /[zZ]$|[+-]\d\d:?\d\d$/.test(completedAt) ? completedAt : `${completedAt}Z`
  const ms = Date.parse(iso)
  return Number.isFinite(ms) ? ms : null
}

/**
 * Sessions whose results were shown on this device since the app started. Reopening one goes
 * straight to the results instead of offering Finish again. Memory only, on purpose: nothing to
 * persist, and an app restart falls back on {@link JUST_ENDED_MS}.
 */
const resultsShown = new Set<string>()

export function markResultsShown(sessionId: string | null | undefined): void {
  if (sessionId) resultsShown.add(sessionId.toLowerCase())
}

export function wereResultsShown(sessionId: string | null | undefined): boolean {
  return !!sessionId && resultsShown.has(sessionId.toLowerCase())
}

/**
 * Sessions this device started, or saw running, since the page loaded (see `EndingInput.liveHere`).
 * Memory only, like {@link markResultsShown}. Marked when a run is started or resumed here, before
 * the player reads it, so an adventure that ends inside the request that starts it still gets its
 * ending even when this device's clock and the server's disagree.
 */
const liveHere = new Set<string>()

export function markLiveHere(sessionId: string | null | undefined): void {
  if (sessionId) liveHere.add(sessionId.toLowerCase())
}

export function isLiveHere(sessionId: string | null | undefined): boolean {
  return !!sessionId && liveHere.has(sessionId.toLowerCase())
}
